import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { AlertController, LoadingController } from "@ionic/angular";
import { PictureApiService } from "src/app/core/api/picture-api.service";
import { IAddOrderPictureResponse } from "src/app/core/models/picture.model";
import { ActionSheetService } from "src/app/shared/components/action-sheet/action-sheet.service";
import { PicturesAddActionSheetComponent } from "src/app/shared/components/pictures-add-actionsheet/pictures-add-actionsheet.component";

import { ToastService } from "../../core/services/toast.service";
import { Device } from "@capacitor/device";
import { StorageService } from "../../core/services/storage.service";

@Injectable()
export class OrderLinePictureService {
	constructor(
		private alertController: AlertController,
		private pictureApiService: PictureApiService,
		private toastService: ToastService,
		private actionSheetService: ActionSheetService,
		private loadingController: LoadingController,
		private router: Router,
		private storageService: StorageService
	) {}

	addOrderPicture(orderId: string, orderLineId: string, orderPictureIds?: string[], componentName?: string): Promise<string[]> {
		return new Promise(async (resolve, reject) => {
			try {
				const actionPhotoAdd = this.actionSheetService.create(PicturesAddActionSheetComponent, {
					module: "WorkorderLinesPictureAdd",
					orderPictureIds,
					componentName
				});
				actionPhotoAdd.subscribe(async (value: string) => {
					if (!value && orderId && orderLineId) {
						// User cancelled the action sheet
						// navigate to the orderline edit page
						this.router.navigate(["/workorders/" + orderId + "/edit/orderlines/" + orderLineId + "/edit"]);
						resolve([]);
						return;
					}
					const deviceInfo = await Device.getInfo();
					switch (value) {
						case "makePhoto":
							// check if camera has permission only on iOS and Android
							if (deviceInfo.platform !== "web") {
								// check if camera permission is granted
								const cameraPermission = await Camera.checkPermissions();

								if (cameraPermission.camera !== "granted") {
									// request camera permission
									const requestPermission = await Camera.requestPermissions({ permissions: ["camera"] });
									// check if camera permission is granted
									if (requestPermission.camera !== "granted") {
										this.toastService.presentFailureToast("Geen toegang tot camera, Controleer je instellingen");
										return;
									}
								}
							}
							const loadingMakePhoto = await this.loadingController.create({
								spinner: "circles",
								message: "Uploaden...",
								translucent: true,
								backdropDismiss: false
							});
							const makePhotoPictureIds = [];

							const makePhoto = await Camera.getPhoto({
								quality: 92,
								saveToGallery: false,
								source: CameraSource.Camera,
								resultType: CameraResultType.Base64
							}).catch((error) => {
								console.log(error.message);
							});
							if (makePhoto) {
								const post = this.pictureApiService
									.addOrderPicture({
										content_type: "image/jpeg",
										order_id: orderId,
										order_line_id: orderLineId,
										order_picture_media: makePhoto.base64String
									})
									.toPromise();
								await loadingMakePhoto.present();
								await post.then(
									(addOrderPictureResponse: IAddOrderPictureResponse) => {
										makePhotoPictureIds.push(addOrderPictureResponse.data[0].order_picture_id);
										this.toastService.presentSuccessToast("Foto toegevoegd");
										resolve(makePhotoPictureIds);
									},
									(error) => {
										this.toastService.presentFailureToast(error.error.errorMessage);
										reject(error);
									}
								);
								await loadingMakePhoto.dismiss();
							}

							break;

						case "selectPhoto":
							// check if camera has permission only on iOS and Android
							if (deviceInfo.platform !== "web") {
								// check if camera permission is granted
								const cameraPermission = await Camera.checkPermissions();

								if (cameraPermission.photos !== "granted") {
									// request camera permission
									const requestPermission = await Camera.requestPermissions({ permissions: ["photos"] });
									// check if camera permission is granted
									if (requestPermission.photos !== "granted") {
										this.toastService.presentFailureToast("Geen toegang tot foto's, Controleer je instellingen");
										return;
									}
								}
							}
							const selectPhotoPictureIds = [];
							const loadingSelectPhoto = await this.loadingController.create({
								spinner: "circles",
								message: "Uploaden...",
								translucent: true,
								backdropDismiss: false
							});

							const selectPhoto = await Camera.pickImages({
								quality: 92
							}).catch((error) => {
								console.log(error.message);
							});

							let count = 0;
							await loadingSelectPhoto.present();
							if (selectPhoto) {
								for (const photo of selectPhoto.photos) {
									const post = this.pictureApiService
										.addOrderPicture({
											content_type: "image/jpeg",
											order_id: orderId,
											order_line_id: orderLineId,
											order_picture_media: await this.getBase64ImageFromBlobURL(photo.webPath)
										})
										.toPromise();

									await post.then(
										(addOrderPictureResponse: IAddOrderPictureResponse) => {
											count += 1;
											selectPhotoPictureIds.push(addOrderPictureResponse.data[0].order_picture_id);
										},
										(error) => {
											this.toastService.presentFailureToast(error.error.errorMessage);
											reject(error);
										}
									);
								}

								resolve(selectPhotoPictureIds);
								await loadingSelectPhoto.dismiss();
								this.toastService.presentSuccessToast(`Foto(s) toegevoegd: ${count}/${selectPhoto.photos.length}`);
							} else {
								resolve([]);
								console.log("Geen foto's geselecteerd");
								await loadingSelectPhoto.dismiss();
							}

							break;

						case "orderlinePhotos":
							this.router.navigate([`/workorders/${orderId}/edit/orderlines/${orderLineId}/pictures`]);
							break;

						case "orderlinePhotosreorder":
							this.router.navigate([`/workorders/${orderId}/edit/orderlines/${orderLineId}/pictures/reorder`]);
							break;
					}
				});
			} catch (error) {
				reject(error);
			}
		});
	}

	getBase64ImageFromBlobURL(img: string): Promise<string> {
		return new Promise((resolve) => {
			const xhr = new XMLHttpRequest();
			xhr.onload = () => {
				const reader = new FileReader();
				reader.onloadend = () => {
					resolve(reader.result.toString().split(",")[1]);
				};
				reader.readAsDataURL(xhr.response);
			};
			xhr.open("GET", img);
			xhr.responseType = "blob";
			xhr.send();
		});
	}

	presentRemoveOrderPictureAlert(): Promise<boolean> {
		return new Promise(async (resolve) => {
			const alert = await this.alertController.create({
				header: "Foto verwijderen",
				message: "Weet je zeker dat je deze foto wilt verwijderen?",
				buttons: [
					{
						text: "Nee",
						role: "cancel",
						handler: () => {
							resolve(false);
						}
					},
					{
						text: "Ja",
						handler: () => {
							resolve(true);
						}
					}
				]
			});
			await alert.present();
		});
	}

	addTempOrderPictureToStore(): Promise<string[]> {
		return new Promise(async (resolve, reject) => {
			try {
				const actionPhotoAdd = this.actionSheetService.create(PicturesAddActionSheetComponent, {
					module: "WorkorderLinesPictureAdd"
				});

				actionPhotoAdd.subscribe(async (value: string) => {
					if (!value) {
						// User cancelled the action sheet
						resolve([]);
						return;
					}

					const deviceInfo = await Device.getInfo();
					const tempPictureIds: string[] = [];

					switch (value) {
						case "makePhoto":
							// check if camera has permission only on iOS and Android
							if (deviceInfo.platform !== "web") {
								// check if camera permission is granted
								const cameraPermission = await Camera.checkPermissions();

								if (cameraPermission.camera !== "granted") {
									// request camera permission
									const requestPermission = await Camera.requestPermissions({ permissions: ["camera"] });
									// check if camera permission is granted
									if (requestPermission.camera !== "granted") {
										this.toastService.presentFailureToast("Geen toegang tot camera, Controleer je instellingen");
										return;
									}
								}
							}

							const loadingMakePhoto = await this.loadingController.create({
								spinner: "circles",
								message: "Foto voorbereiden...",
								translucent: true,
								backdropDismiss: false
							});

							const makePhoto = await Camera.getPhoto({
								quality: 92,
								saveToGallery: false,
								source: CameraSource.Camera,
								resultType: CameraResultType.Base64
							}).catch((error) => {
								console.log(error.message);
							});

							if (makePhoto) {
								await loadingMakePhoto.present();

								// Create a temporary picture ID and store the base64 data
								const tempPictureId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
								const tempPictureData = {
									id: tempPictureId,
									base64: makePhoto.base64String,
									contentType: "image/jpeg",
									timestamp: Date.now()
								};

								// Store in temporary storage
								const existingTempPictures = (await this.storageService.get("tempOrderPictures")) || [];
								existingTempPictures.push(tempPictureData);
								await this.storageService.set("tempOrderPictures", existingTempPictures);

								tempPictureIds.push(tempPictureId);
								await loadingMakePhoto.dismiss();
								this.toastService.presentSuccessToast("Foto toegevoegd aan tijdelijke opslag");
								resolve(tempPictureIds);
							} else {
								await loadingMakePhoto.dismiss();
								resolve([]);
							}
							break;

						case "selectPhoto":
							// check if camera has permission only on iOS and Android
							if (deviceInfo.platform !== "web") {
								// check if camera permission is granted
								const cameraPermission = await Camera.checkPermissions();

								if (cameraPermission.photos !== "granted") {
									// request camera permission
									const requestPermission = await Camera.requestPermissions({ permissions: ["photos"] });
									// check if camera permission is granted
									if (requestPermission.photos !== "granted") {
										this.toastService.presentFailureToast("Geen toegang tot foto's, Controleer je instellingen");
										return;
									}
								}
							}

							const loadingSelectPhoto = await this.loadingController.create({
								spinner: "circles",
								message: "Foto's voorbereiden...",
								translucent: true,
								backdropDismiss: false
							});

							const selectPhoto = await Camera.pickImages({
								quality: 92
							}).catch((error) => {
								console.log(error.message);
							});

							if (selectPhoto && selectPhoto.photos.length > 0) {
								await loadingSelectPhoto.present();

								for (const photo of selectPhoto.photos) {
									const base64Image = await this.getBase64ImageFromBlobURL(photo.webPath);
									const tempPictureId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
									const tempPictureData = {
										id: tempPictureId,
										base64: base64Image,
										contentType: "image/jpeg",
										timestamp: Date.now()
									};

									// Store in temporary storage
									const existingTempPictures = (await this.storageService.get("tempOrderPictures")) || [];
									existingTempPictures.push(tempPictureData);
									await this.storageService.set("tempOrderPictures", existingTempPictures);

									tempPictureIds.push(tempPictureId);
								}

								await loadingSelectPhoto.dismiss();
								this.toastService.presentSuccessToast(`${tempPictureIds.length} foto(s) toegevoegd aan tijdelijke opslag`);
								resolve(tempPictureIds);
							} else {
								await loadingSelectPhoto.dismiss();
								resolve([]);
							}
							break;

						default:
							resolve([]);
							break;
					}
				});
			} catch (error) {
				reject(error);
			}
		});
	}

	// Helper method to post temporary pictures to API when orderline is created
	async postTempPicturesToAPI(orderId: string, orderLineId: string, tempPictureIds: string[]): Promise<string[]> {
		const realPictureIds: string[] = [];

		if (!tempPictureIds || tempPictureIds.length === 0) {
			return realPictureIds;
		}

		try {
			const tempPictures = (await this.storageService.get("tempOrderPictures")) || [];

			for (const tempPictureId of tempPictureIds) {
				const tempPicture = tempPictures.find((p) => p.id === tempPictureId);
				if (tempPicture) {
					try {
						const response = (await this.pictureApiService
							.addOrderPicture({
								content_type: tempPicture.contentType,
								order_id: orderId,
								order_line_id: orderLineId,
								order_picture_media: tempPicture.base64
							})
							.toPromise()) as IAddOrderPictureResponse;

						realPictureIds.push(response.data[0].order_picture_id);
					} catch (error) {
						console.error("Error fotos opslaan:", error);
						// Continue with other pictures even if one fails
					}
				}
			}

			// Clean up temp pictures from storage
			const remainingTempPictures = tempPictures.filter((p) => !tempPictureIds.includes(p.id));
			await this.storageService.set("tempOrderPictures", remainingTempPictures);
		} catch (error) {
			console.error("Error fotos opslaan:", error);
		}

		return realPictureIds;
	}

	// Helper method to clean up old temporary pictures (older than 24 hours)
	async cleanupOldTempPictures(): Promise<void> {
		try {
			const tempPictures = (await this.storageService.get("tempOrderPictures")) || [];
			const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;

			const validTempPictures = tempPictures.filter((p) => p.timestamp > oneDayAgo);
			await this.storageService.set("tempOrderPictures", validTempPictures);
		} catch (error) {
			console.error("Error tijdelijke foto's opslaan:", error);
		}
	}

	// Helper method to clean up specific temporary pictures by their IDs
	async cleanupSpecificTempPictures(tempPictureIds: string[]): Promise<void> {
		try {
			if (!tempPictureIds || tempPictureIds.length === 0) {
				return;
			}

			const tempPictures = (await this.storageService.get("tempOrderPictures")) || [];
			const remainingTempPictures = tempPictures.filter((p) => !tempPictureIds.includes(p.id));
			await this.storageService.set("tempOrderPictures", remainingTempPictures);
		} catch (error) {
			console.error("Error tijdelijke foto's opslaan:", error);
		}
	}

	removeOrderPicture(orderPictureId: string): Promise<any> {
		return new Promise((resolve, reject) => {
			this.pictureApiService.removeOrderPicture(orderPictureId).subscribe(
				() => {
					this.toastService.presentSuccessToast("Foto verwijderd");
					resolve(orderPictureId);
				},
				(error) => {
					this.toastService.presentFailureToast(error.error.errorMessage);
					reject(error);
				}
			);
		});
	}

	reorderOrderPictures(pictureIds: string[]) {
		this.pictureApiService.reorderPictures(pictureIds).subscribe(
			() => {
				this.toastService.presentSuccessToast("Foto's herordend");
			},
			(error) => {
				this.toastService.presentFailureToast(error.error.errorMessage);
			}
		);
	}
}
